// The masjid's uploaded files: search, sort, preview, copy a link, download,
// and delete one or many at once.
import React, { useMemo, useState } from 'react';
import {
    Image, Video, AudioLines, FileText, Search, UploadCloud, Grid, List, Download, Trash2, Link2, X, Check, Loader2,
} from 'lucide-react';
import { Header } from '../../../components/page-header/Header';
import UploadModal from '../../../components/Modal/UploadModal';
import { useBoolean } from '../../../hooks/use-boolean';
import DeleteModal from '../../../components/table/components/delete-modal';
import { useGetresourcesQuery } from '../../../store/Reducer/resources';
import { useDeleteFileMutation } from '../../../store/Reducer/file';
import toast from 'react-hot-toast';

const TABS = [
    { id: 'images', label: 'Images', icon: Image, upload: 'image' },
    { id: 'videos', label: 'Videos', icon: Video, upload: 'video' },
    { id: 'audios', label: 'Audios', icon: AudioLines, upload: 'audio' },
    { id: 'others', label: 'Documents', icon: FileText, upload: 'document' },
];

const SORTS = {
    newest: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    oldest: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    name: (a, b) => nameOf(a).localeCompare(nameOf(b)),
    largest: (a, b) => (b.fileSize || 0) - (a.fileSize || 0),
};

// The name it had on the owner's computer; the stored name is a random id.
const nameOf = (item) => item.fileNameHR || item.fileName;

const Thumb = ({ item, tab, size }) => {
    const box = size === 'large' ? 'w-full aspect-[4/3]' : 'w-14 h-14 shrink-0';
    if (tab === 'images') return <img src={item.fileUrl} alt={nameOf(item)} loading="lazy" className={`${box} object-cover rounded-lg bg-gray-100`} />;
    if (tab === 'videos') return <video src={item.fileUrl} preload="metadata" className={`${box} object-cover rounded-lg bg-gray-900`} />;
    const Icon = tab === 'audios' ? AudioLines : FileText;
    return <div className={`${box} flex items-center justify-center rounded-lg bg-gray-100`}><Icon className="w-8 h-8 text-gray-400" /></div>;
};

const Preview = ({ item, tab, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
        <div role="dialog" aria-modal="true" aria-label={nameOf(item)} className="relative w-full max-w-3xl rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Close" onClick={onClose} className="absolute right-3 top-3 rounded p-1 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
            <p className="mb-3 pr-8 font-semibold text-gray-800 break-all">{nameOf(item)}</p>
            {tab === 'images' && <img src={item.fileUrl} alt={nameOf(item)} className="max-h-[70vh] w-full rounded-lg object-contain" />}
            {tab === 'videos' && <video src={item.fileUrl} controls className="max-h-[70vh] w-full rounded-lg" />}
            {tab === 'audios' && <audio src={item.fileUrl} controls className="w-full" />}
            {tab === 'others' && (/\.pdf$/i.test(item.fileName)
                ? <iframe title={nameOf(item)} src={item.fileUrl} className="h-[70vh] w-full rounded-lg border" />
                : <a href={item.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open the file</a>)}
            <p className="mt-3 text-xs text-gray-500">{item.fileSizeHR} · uploaded {new Date(item.createdAt).toLocaleDateString()}</p>
        </div>
    </div>
);

const ResourcesMediaManagement = () => {
    // ?tab=videos opens that tab (links from the Resources page).
    const [activeTab, setActiveTab] = useState(() => {
        const tab = new URLSearchParams(window.location.search).get('tab');
        return TABS.some((t) => t.id === tab) ? tab : 'images';
    });
    const [viewMode, setViewMode] = useState('grid');
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState('newest');
    const [selected, setSelected] = useState(() => new Set()); // stored file names
    const [toDelete, setToDelete] = useState([]); // file names waiting for confirmation
    const [deleting, setDeleting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [copied, setCopied] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const { data, isLoading, isError, refetch } = useGetresourcesQuery({});
    const [deleteFile] = useDeleteFileMutation();
    const confirm = useBoolean();

    const mediaData = data?.data || { images: [], videos: [], audios: [], others: [] };
    const tab = TABS.find((t) => t.id === activeTab);
    const items = useMemo(() => {
        const q = query.trim().toLowerCase();
        return [...(mediaData[activeTab] || [])]
            .filter((item) => !q || nameOf(item).toLowerCase().includes(q))
            .sort(SORTS[sort]);
    }, [mediaData, activeTab, query, sort]);

    const breadcrumbItems = [
        { label: 'Dashboard', to: '/user-dashboard' },
        { label: 'Resources', to: '/user-dashboard/resources' },
        { label: 'Media Management' },
    ];

    const switchTab = (id) => {
        setActiveTab(id);
        setSelected(new Set());
    };

    const toggle = (name) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });
    const allSelected = items.length > 0 && items.every((item) => selected.has(item.fileName));
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((item) => item.fileName)));

    const copyLink = async (item) => {
        try {
            await navigator.clipboard.writeText(item.fileUrl);
            setCopied(item.fileName);
            setTimeout(() => setCopied(null), 1500);
            toast.success('Link copied');
        } catch (e) {
            toast.error("Couldn't copy the link");
        }
    };

    const download = (item) => {
        const link = document.createElement('a');
        link.href = item.fileUrl;
        link.download = nameOf(item);
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const askDelete = (names) => {
        setToDelete(names);
        confirm.onTrue();
    };

    const handleDelete = async () => {
        confirm.onFalse();
        setDeleting(true);
        let failed = 0;
        for (const name of toDelete) {
            try {
                await deleteFile(name).unwrap();
            } catch (error) {
                failed += 1;
            }
        }
        setDeleting(false);
        setSelected((prev) => new Set([...prev].filter((name) => !toDelete.includes(name))));
        refetch();
        const done = toDelete.length - failed;
        if (failed) toast.error(`${failed} file${failed > 1 ? 's' : ''} couldn't be deleted`);
        if (done) toast.success(`${done} file${done > 1 ? 's' : ''} deleted`);
    };

    const actions = (item, small) => (
        <div className="flex items-center gap-1">
            <button type="button" title="Copy link" aria-label={`Copy link to ${nameOf(item)}`} onClick={() => copyLink(item)} className="rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800">
                {copied === item.fileName ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
            </button>
            <button type="button" title="Download" aria-label={`Download ${nameOf(item)}`} onClick={() => download(item)} className="rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800">
                <Download className={small ? 'w-4 h-4' : 'w-5 h-5'} />
            </button>
            <button type="button" title="Delete" aria-label={`Delete ${item.fileName}`} onClick={() => askDelete([item.fileName])} className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600">
                <Trash2 className={small ? 'w-4 h-4' : 'w-5 h-5'} />
            </button>
        </div>
    );

    const checkbox = (item) => (
        <input
            type="checkbox"
            aria-label={`Select ${nameOf(item)}`}
            checked={selected.has(item.fileName)}
            onChange={() => toggle(item.fileName)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600"
        />
    );

    return (
        <>
            <section className="min-h-[86vh] bg-gradient-to-b from-slate-50 to-gray-50">
                <div className="max-w-7xl mx-auto p-4 sm:p-6">
                    <div className="mb-6 flex items-center justify-between">
                        <Header title="Media Management" breadcrumbItems={breadcrumbItems} />
                    </div>

                    <div className="bg-white rounded-lg shadow-md mb-4">
                        <div role="tablist" className="flex border-b flex-wrap">
                            {TABS.map((t) => (
                                <button
                                    key={t.id}
                                    role="tab"
                                    aria-selected={activeTab === t.id}
                                    className={`flex items-center px-3 sm:px-6 py-3 text-xs sm:text-sm font-medium ${activeTab === t.id ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    onClick={() => switchTab(t.id)}
                                >
                                    <t.icon className="w-5 h-5 mr-2" />
                                    {t.label}
                                    <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 text-xs text-gray-500">{(mediaData[t.id] || []).length}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                                onClick={() => setIsUploadModalOpen(true)}
                            >
                                <UploadCloud className="w-5 h-5 mr-2" />
                                Upload {tab.upload}
                            </button>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <input
                                    type="search"
                                    aria-label="Search files"
                                    placeholder="Search by name"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    className="w-56 rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
                                />
                            </div>
                            <select aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
                                <option value="newest">Newest first</option>
                                <option value="oldest">Oldest first</option>
                                <option value="name">Name A–Z</option>
                                <option value="largest">Largest first</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <button aria-label="List view" aria-pressed={viewMode === 'list'} className={`p-2 ${viewMode === 'list' ? 'bg-gray-200' : 'bg-white'} text-gray-600 rounded-md hover:bg-gray-300`} onClick={() => setViewMode('list')}>
                                <List className="w-5 h-5" />
                            </button>
                            <button aria-label="Grid view" aria-pressed={viewMode === 'grid'} className={`p-2 ${viewMode === 'grid' ? 'bg-gray-200' : 'bg-white'} text-gray-600 rounded-md hover:bg-gray-300`} onClick={() => setViewMode('grid')}>
                                <Grid className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {items.length > 0 && (
                        <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
                            <label className="flex items-center gap-2 text-gray-600">
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                                Select all
                            </label>
                            {selected.size > 0 && (
                                <button type="button" onClick={() => askDelete([...selected])} disabled={deleting}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 font-medium text-white hover:bg-red-700 disabled:opacity-60">
                                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                    Delete {selected.size} selected
                                </button>
                            )}
                        </div>
                    )}

                    <div className={`bg-white rounded-lg shadow-md p-3 sm:p-6 ${viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4' : 'space-y-3'}`}>
                        {isLoading ? (
                            <div className="col-span-full flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
                        ) : isError ? (
                            <div className="col-span-full text-center py-4 text-red-500">Error loading media files</div>
                        ) : items.length === 0 ? (
                            <div className="col-span-full text-center py-4 text-gray-500">
                                {query ? `No ${tab.label.toLowerCase()} match "${query}"` : `No ${tab.label.toLowerCase()} yet`}
                            </div>
                        ) : viewMode === 'grid' ? (
                            items.map((item) => (
                                <div key={item.fileName} className={`relative rounded-lg border p-2 ${selected.has(item.fileName) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                                    <div className="absolute left-3 top-3 z-10 rounded bg-white/90 p-0.5">{checkbox(item)}</div>
                                    <button type="button" onClick={() => setPreview(item)} aria-label={`Preview ${nameOf(item)}`} className="block w-full">
                                        <Thumb item={item} tab={activeTab} size="large" />
                                    </button>
                                    <p className="mt-2 truncate text-sm font-medium text-gray-800" title={nameOf(item)}>{nameOf(item)}</p>
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs text-gray-500">{item.fileSizeHR || ''}</p>
                                        {actions(item, true)}
                                    </div>
                                </div>
                            ))
                        ) : (
                            items.map((item) => (
                                <div key={item.fileName} className={`flex flex-wrap items-center justify-between gap-3 rounded-lg p-3 ${selected.has(item.fileName) ? 'bg-blue-50' : 'bg-gray-50'}`}>
                                    <div className="flex min-w-0 items-center gap-3">
                                        {checkbox(item)}
                                        <button type="button" onClick={() => setPreview(item)} aria-label={`Preview ${nameOf(item)}`}><Thumb item={item} tab={activeTab} /></button>
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-medium text-gray-800" title={nameOf(item)}>{nameOf(item)}</p>
                                            <p className="text-xs text-gray-500">{item.fileSizeHR || ''} • {new Date(item.createdAt).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    {actions(item)}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>

            {preview && <Preview item={preview} tab={activeTab} onClose={() => setPreview(null)} />}

            <UploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                mediaType={tab.upload}
                onSuccess={() => refetch()}
            />

            <DeleteModal
                isOpen={confirm.value}
                title={toDelete.length > 1 ? `Delete ${toDelete.length} files?` : 'Are You Sure?'}
                desc="If a file is used in an announcement, event or on your website, it will stop showing there. This can't be undone."
                onConfirm={handleDelete}
                onCancel={() => confirm.onFalse()}
            />
        </>
    );
};

export default ResourcesMediaManagement;
